import { cn } from '@/lib/utils';

import { TotemLabel } from '../TotemLabel';
import { BasicInput } from '../BasicInput';
import { Controller } from 'react-hook-form';
import type { Control, FieldPath, FieldPathValue, FieldValues } from 'react-hook-form';

import DatePicker, { registerLocale } from 'react-datepicker';
import type { DatePickerProps } from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

import { fr } from 'date-fns/locale';

registerLocale('fr', fr);

const isRangeDate = (date: Date | Date[] | null) => {
    return Array.isArray(date);
};

// Générique sur la forme du formulaire : une page qui type son `useForm` peut passer son `control` et
// son `name` sans les élargir (react-hook-form ne rend pas `Control<T>` assignable à `Control`).
type DetailFormDatePickerProps<
    TFieldValues extends FieldValues = FieldValues,
    TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = Omit<DatePickerProps, 'onChange' | 'selectsMultiple' | 'selectsRange'> & {
    label?: string;
    sublabel?: string;
    defaultValue?: FieldPathValue<TFieldValues, TName>;
    placeholder?: string;
    minDate?: Date;
    name: TName;
    isDisabled?: boolean;
    error?: string;
    control: Control<TFieldValues>;
    transformValueToDate?: (value: string | null) => Date | null;
    transformDateToValue?: (date: Date | null) => string | null;
    dateFormat?: string;
    compact?: boolean;
    dataTestId?: string;
};

export const DetailFormDatePicker = <
    TFieldValues extends FieldValues = FieldValues,
    TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
    label,
    sublabel,
    placeholder,
    defaultValue,
    name,
    isDisabled,
    error,
    control,
    minDate,
    transformValueToDate,
    transformDateToValue,
    dateFormat = 'dd/MM/yyyy',
    compact = false,
    dataTestId = `date-picker-${name}`,
    ...props
}: DetailFormDatePickerProps<TFieldValues, TName>) => {
    return (
        <div className="flex flex-col text-foreground" data-testid={dataTestId}>
            <div className="flex flex-col">
                {label ? <TotemLabel>{label}</TotemLabel> : null}
                {sublabel ? <span className="text-muted-foreground text-[13px]">{sublabel}</span> : null}
            </div>
            <div className="relative mt-[5px]">
                <Controller
                    control={control}
                    name={name}
                    defaultValue={defaultValue}
                    render={({ field: { onChange, value } }) => (
                        <DatePicker
                            selected={transformValueToDate ? transformValueToDate(value) : value}
                            onChange={(date: any) => {
                                if (isRangeDate(date)) {
                                    return;
                                }
                                if (transformDateToValue) {
                                    onChange(transformDateToValue(date as Date | null));
                                } else {
                                    onChange(date);
                                }
                            }}
                            placeholderText={placeholder}
                            locale="fr"
                            disabled={isDisabled}
                            dateFormat={dateFormat}
                            customInput={<BasicInput className={cn(compact && 'px-2 py-1.5 text-base h-8')} />}
                            minDate={minDate}
                            wrapperClassName={`totem-date-picker-${name}`}
                            {...props}
                        />
                    )}
                />
            </div>
            {/* Sous le champ, dans le flux : positionnée en absolu au-dessus, l'erreur (`w-max`) débordait
                sur le libellé dès que la colonne était plus étroite que le message. */}
            {error ? <div className="mt-1 text-sm text-destructive">{error}</div> : null}
        </div>
    );
};
